import { DETAILED, HOME_PAGE_DETAILED, SLUGS } from "@components/home/routes";
import ArticleView from "@components/pages/article/ArticleView";
import LocationPageView from "@components/pages/location/components/LocationPageView";
import ContactUs from "@components/pages/page/components/ContactUs";
import PageView from "@components/pages/page/components/PageView";
import { RAQ_FORM_TYPES, RaqFormTypes } from "@components/utils/constants";
import LandingPageView from "@modules/landing-page/LandingPageView";
import fetchGet from "@services/api/fetchGet";
import baseApiCalls from "@services/baseApiCalls";
import detectRedirect from "@services/detectRedirect";
import { GetStaticPaths, GetStaticProps } from "next";
import { IDefaultPagePropsResponse } from "../types/commonResponses";
import { PAGE_TYPES, PageTypes } from "../types/index";
import {
  IArticleResponse,
  IContactUsResponse,
  ILandingPageResponse,
  ILocationResponse,
  IPageResponse,
} from "../types/modelResponses";

interface ILocationOrGenericPageDetailsPage extends IDefaultPagePropsResponse {
  location: ILocationResponse;
  landingPage: ILandingPageResponse;
  contactUs: IContactUsResponse;
  page: IPageResponse;
  article: IArticleResponse;
  pageType: PageTypes;
  raqFormType: RaqFormTypes;
  ogImage: string;
}

function LocationOrLandingPage({
  location,
  topNavigation,
  landingPage,
  contactUs,
  pageType,
  page,
  article,
  raqFormType,
  ogImage,
}: ILocationOrGenericPageDetailsPage): JSX.Element | null {
  switch (pageType) {
    case PAGE_TYPES.Location:
      return (
        <LocationPageView
          location={location}
          topNavigation={topNavigation}
          raqFormType={raqFormType}
        />
      );
    case PAGE_TYPES.LandingPage:
      return (
        <LandingPageView
          landingPage={landingPage}
          topNavigation={topNavigation}
          raqFormType={raqFormType}
        />
      );
    case PAGE_TYPES.Page:
      return (
        <PageView
          page={page}
          topNavigation={topNavigation}
          raqFormType={raqFormType}
          ogImage={ogImage}
        />
      );
    case PAGE_TYPES.Article:
      return (
        <ArticleView
          article={article}
          topNavigation={topNavigation}
          raqFormType={raqFormType}
          ogImage={ogImage}
        />
      );
    case PAGE_TYPES.ContactPage:
      return <ContactUs contactUs={contactUs} />;
    default:
      return null;
  }
}

export default LocationOrLandingPage;

export const getStaticPaths: GetStaticPaths = async () => {
  /*
  const landingsRes = await fetchGet(SLUGS.LANDING_PAGES);
  const landingPages = await landingsRes.json();

  const locationsRes = await fetchGet(SLUGS.LOCATIONS);
  const locations = await locationsRes.json();

  const pagesRes = await fetchGet(SLUGS.PAGES);
  const pages = await pagesRes.json();

  const articlesRes = await fetchGet(SLUGS.ARTICLES);
  const articles = await articlesRes.json();

  const contactUsRes = await fetchGet(SLUGS.CONTACTUS);
  const contactUs = await contactUsRes.json();

  const contact = [contactUs.data.attributes];
  */

  const [landingPages, locations, pages, articles, contactUs] =
    await Promise.all([
      fetchGet(SLUGS.LANDING_PAGES).then((res) => res.json()),
      fetchGet(SLUGS.LOCATIONS).then((res) => res.json()),
      fetchGet(SLUGS.PAGES).then((res) => res.json()),
      fetchGet(SLUGS.ARTICLES).then((res) => res.json()),
      fetchGet(SLUGS.CONTACTUS).then((res) => res.json()),
    ]);

  const contact = [contactUs.data.attributes];

  const paths = [
    ...landingPages,
    ...locations,
    ...pages,
    ...articles,
    ...contact,
  ].map((e) => ({
    params: { slug: e.SEO.Slug.split("/") },
  }));

  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  console.time();
  let pageFound = 0;
  let hasInternalError = false;

  const redirect = await detectRedirect(
    `${Array.isArray(params?.slug) ? params?.slug.join("/") : params?.slug}`
  );
  if (redirect) {
    return redirect;
  }

  const checkResponseCode = async (
    res: Response,
    options?: { isContactUs?: boolean }
  ): Promise<any | undefined> => {
    if (res.status >= 500) {
      hasInternalError = true;
      return undefined;
    } else if (res.status < 400) {
      if (options?.isContactUs) {
        // If the page is contact us - custom logic
        const contactUsTMP = await res.json();
        if (
          Array.isArray(params?.slug) &&
          contactUsTMP.data.attributes.SEO.Slug === params?.slug[0]
        ) {
          pageFound++;
          return contactUsTMP;
        }
      } else {
        // Every page except contact-us
        pageFound++;
        return await res.json();
      }
    }
    return undefined;
  };

  /*
  const locationRes = await fetchGet(
    `${DETAILED.LOCATIONS}${
      Array.isArray(params?.slug) ? params?.slug.join("@") : ""
    }`
  );

  const location = await checkResponseCode(locationRes);

  const landingRes = await fetchGet(
    `${DETAILED.LANDING_PAGES}${
      Array.isArray(params?.slug) ? params?.slug.join("@") : ""
    }`
  );

  const landingPage = await checkResponseCode(landingRes);

  const pagesRes = await fetchGet(
    `${DETAILED.PAGES}${
      Array.isArray(params?.slug) ? params?.slug.join("@") : ""
    }`
  );
  const page = await checkResponseCode(pagesRes);

  const articlesRes = await fetchGet(
    `${DETAILED.ARTICLES}${
      Array.isArray(params?.slug) ? params?.slug.join("@") : ""
    }`
  );
  const article = await checkResponseCode(articlesRes);
  */

  const [location, landingPage, page, article] = await Promise.all([
    fetchGet(
      `${DETAILED.LOCATIONS}${
        Array.isArray(params?.slug) ? params?.slug.join("@") : ""
      }`
    ).then((res) => checkResponseCode(res)),
    fetchGet(
      `${DETAILED.LANDING_PAGES}${
        Array.isArray(params?.slug) ? params?.slug.join("@") : ""
      }`
    ).then((res) => checkResponseCode(res)),
    fetchGet(
      `${DETAILED.PAGES}${
        Array.isArray(params?.slug) ? params?.slug.join("@") : ""
      }`
    ).then((res) => checkResponseCode(res)),
    fetchGet(
      `${DETAILED.ARTICLES}${
        Array.isArray(params?.slug) ? params?.slug.join("@") : ""
      }`
    ).then((res) => checkResponseCode(res)),
  ]);

  /**
   * DO BE FIXED WITH PHASE2 OF NAMU, WITH MULTITENANT STRAPI
   */

  const contactUsRes = await fetchGet(SLUGS.CONTACTUS);
  let contactUs;
  if (contactUsRes.status < 400) {
    const contactUsTMP = await contactUsRes.json();
    if (
      Array.isArray(params?.slug) &&
      contactUsTMP.data.attributes.SEO.Slug === params?.slug[0]
    ) {
      contactUs = contactUsTMP;
      pageFound++;
    }
  }

  if (pageFound === 0) {
    return {
      notFound: true,
      revalidate: Number(process.env.REVALIDATE_TIMER),
    };
  }

  const {
    footer,
    mediaAssets,
    searchManagementRes,
    topNavigation,
    menus,
    usp,
  } = await baseApiCalls();

  let pageType = null;
  if (location) {
    pageType = PAGE_TYPES.Location;
  }
  if (landingPage) {
    pageType = PAGE_TYPES.LandingPage;
  }
  if (page) {
    pageType = PAGE_TYPES.Page;
  }
  if (article) {
    pageType = PAGE_TYPES.Article;
  }
  if (contactUs) {
    pageType = PAGE_TYPES.ContactPage;
  }
  if (hasInternalError) {
    pageType = PAGE_TYPES.InternalErrorPage;
  }

  //const raqFormData = await fetchGet(RAQ_FORM);
  //const raqForm = await raqFormData.json();

  const homePageData = await fetchGet(HOME_PAGE_DETAILED);
  const homePage = await homePageData.json();

  const heroImage = homePage.Hero_Media?.[0];
  console.timeEnd();
  return {
    notFound: pageType === null,
    props: {
      footer,
      location: location || null,
      mediaAssets,
      topNavigation,
      landingPage: landingPage || null,
      page: page || null,
      article: article || null,
      contactUs: contactUs || null,
      pageType,
      menus,
      usp,
      //raqFormType: raqForm?.Form_Type || RAQ_FORM_TYPES.FORM_A,
      raqFormType: RAQ_FORM_TYPES.FORM_B,
      searchManagement: searchManagementRes,
      hasInternalError,
      ogImage: heroImage,
    },
    revalidate: Number(process.env.REVALIDATE_TIMER),
  };
};
